import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { buildPatchPayload, fieldError, type ParsedApiError } from '@loctopus/shared';
import { ErrorSummary } from '@loctopus/ui';
import { api } from '../../lib/api';
import { toParsedError } from '../../lib/errorDisplay';
import type { AddOn } from '../../lib/types';
import {
  Badge,
  Button,
  Card,
  DataTable,
  FormField,
  Input,
  Modal,
  ModuleNotIncluded,
  PageHeader,
  Select,
  Spinner,
} from '../../components/ui';
import { RequirePermission } from '../../lib/auth';
import { useClientPackage } from '../../lib/clientPackage';
import { useEventPicker } from '../shared/useEventPicker';

export function AddOnsPage() {
  const { eventId, picker } = useEventPicker();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<AddOn | null>(null);
  const { hasModule, isLoading: pkgLoading } = useClientPackage();

  const addOnsQuery = useQuery({
    queryKey: ['add-ons', eventId],
    queryFn: () => api.addOns.list(eventId!),
    enabled: !!eventId,
  });

  // docs/ARCHITECTURE_V2.md §3.4/§12 agent 7: courtesy-only gate, see PromoCodesPage.tsx's
  // identical comment.
  if (!pkgLoading && !hasModule('addOns')) {
    return (
      <div>
        <PageHeader title="Add-ons" />
        <ModuleNotIncluded feature="Add-ons" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Add-ons"
        description="Merchandise, parking, donations and other extras buyers can attach to an order."
        actions={
          <RequirePermission permission="add_ons.manage">
            <Button
              disabled={!eventId}
              onClick={() => {
                setEditing(null);
                setModalOpen(true);
              }}
            >
              New add-on
            </Button>
          </RequirePermission>
        }
      />
      <Card title="Event" className="mb-6">
        {picker}
      </Card>
      {!eventId ? (
        <p className="text-sm text-slate-500">Choose an event to manage its add-ons.</p>
      ) : addOnsQuery.isLoading ? (
        <Spinner />
      ) : (
        <DataTable<AddOn>
          columns={[
            { key: 'name', header: 'Name', render: (a) => a.name },
            { key: 'kind', header: 'Kind', render: (a) => <Badge>{a.kind}</Badge> },
            {
              key: 'price',
              header: 'Price',
              render: (a) =>
                a.priceMinor != null
                  ? `${(a.priceMinor / 100).toFixed(2)} ${a.currency}`
                  : 'Buyer enters amount',
            },
            {
              key: 'inventory',
              header: 'Inventory limit',
              render: (a) => a.inventoryLimit ?? 'Unlimited',
            },
          ]}
          rows={addOnsQuery.data?.data ?? []}
          getRowKey={(a) => a.id}
          onRowClick={(a) => {
            setEditing(a);
            setModalOpen(true);
          }}
          emptyMessage="No add-ons for this event yet."
        />
      )}
      {eventId && (
        // REAL BUG FOUND AND FIXED (same root cause as EventDetailPage.tsx's TicketTypeFormModal,
        // found while live-verifying this round's clear-vs-untouched fix): always-mounted modal,
        // useState initializers only ran once at first mount - "Edit add-on" always opened blank.
        // `key` forces a real remount per edited record.
        <AddOnFormModal
          key={editing?.id ?? 'new'}
          eventId={eventId}
          addOn={editing}
          open={modalOpen}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  );
}

function AddOnFormModal({
  eventId,
  addOn,
  open,
  onClose,
}: {
  eventId: string;
  addOn: AddOn | null;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(addOn?.name ?? '');
  const [kind, setKind] = useState<'fixed' | 'donation'>(addOn?.kind ?? 'fixed');
  const [priceMajor, setPriceMajor] = useState(
    addOn?.priceMinor != null ? String(addOn.priceMinor / 100) : '10',
  );
  const [inventoryLimit, setInventoryLimit] = useState(
    addOn?.inventoryLimit != null ? String(addOn.inventoryLimit) : '',
  );
  const [formError, setFormError] = useState<ParsedApiError | null>(null);

  const mutation = useMutation({
    mutationFn: () => {
      const current = {
        name,
        kind,
        currency: 'AUD' as const,
        priceMinor: kind === 'fixed' ? Math.round(Number(priceMajor) * 100) : null,
        inventoryLimit: inventoryLimit ? Number(inventoryLimit) : null,
      };
      if (!addOn) {
        // REAL BUG FOUND AND FIXED: createAddOnSchema's priceMinor/inventoryLimit are both
        // `.optional()` - that accepts a missing key, never an explicit `null`. This always sent
        // both as `null` when not applicable/left blank, so every "donation" add-on (no fixed
        // price) and every add-on left with no inventory limit 400'd. There's no prior value on
        // a CREATE, so each is only included when actually relevant.
        const body: Partial<AddOn> = {
          name: current.name,
          kind: current.kind,
          currency: current.currency,
        };
        if (current.priceMinor !== null) body.priceMinor = current.priceMinor;
        if (current.inventoryLimit !== null) body.inventoryLimit = current.inventoryLimit;
        return api.addOns.create(eventId, body);
      }
      // FIXED (EDIT path - same documented known limitation as the other forms fixed this round,
      // `docs/FORM_SWEEP_RESULTS.md` "Known real gaps" #4): switching an add-on from "fixed" to
      // "donation" never cleared its stale `priceMinor`, and an inventory limit once set could
      // never be lifted back to unlimited - omitting a blank field on edit meant "don't change
      // it". `buildPatchPayload` diffs against the add-on as loaded: unchanged fields are
      // omitted, a field cleared to blank is sent as an explicit `null` (now accepted -
      // `updateAddOnSchema`'s `priceMinor`/`inventoryLimit` are `.nullable().optional()`), a
      // changed field is sent with its new value.
      const original = {
        name: addOn.name,
        kind: addOn.kind,
        currency: addOn.currency,
        priceMinor: addOn.priceMinor ?? null,
        inventoryLimit: addOn.inventoryLimit ?? null,
      };
      return api.addOns.update(addOn.id, buildPatchPayload(original, current));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['add-ons', eventId] });
      onClose();
    },
    onError: (err) => setFormError(toParsedError(err)),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={addOn ? 'Edit add-on' : 'New add-on'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={!name.trim() || mutation.isPending}>
            {mutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </>
      }
    >
      {formError && <ErrorSummary error={formError} className="mb-3" />}
      <FormField label="Name" error={fieldError(formError, 'name')}>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Event T-Shirt" />
      </FormField>
      <FormField label="Kind" error={fieldError(formError, 'kind')}>
        <Select value={kind} onChange={(e) => setKind(e.target.value as 'fixed' | 'donation')}>
          <option value="fixed">Fixed price</option>
          <option value="donation">Donation (buyer enters amount)</option>
        </Select>
      </FormField>
      {kind === 'fixed' && (
        <FormField label="Price" error={fieldError(formError, 'priceMinor')}>
          <Input
            type="number"
            min={0}
            step="0.01"
            value={priceMajor}
            onChange={(e) => setPriceMajor(e.target.value)}
          />
        </FormField>
      )}
      <FormField
        label="Inventory limit"
        hint="Leave blank for unlimited"
        error={fieldError(formError, 'inventoryLimit')}
      >
        <Input
          type="number"
          min={0}
          value={inventoryLimit}
          onChange={(e) => setInventoryLimit(e.target.value)}
        />
      </FormField>
    </Modal>
  );
}
